import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ANSWER_KEYS = ["incident_date", "incident_age_bucket", "at_fault", "currently_represented", "physically_injured", "medical_treatment"];

/**
 * Which firm a shared-funnel page should show right now.
 * liveBuyer: undefined = keep the page's own firm (link firm or neutral),
 * null = no matching firm (neutral page), object = show this firm.
 * The visitor's state comes from the form once picked, otherwise from their location.
 */
export function useLiveBuyer({ page, slug, affParam, sessionId, useLocation }) {
  const [live, setLive] = useState(undefined);
  const keyRef = useRef("");
  const geoRef = useRef(undefined);
  const linkCode = page?.buyer?.code || null;
  const enabled = Boolean(page?.is_global_funnel || page?.buyer_lander) && !page?.is_sample_page;

  const ask = useCallback((body, key, onDone) => {
    axios.post(`${API}/public/funnel/match`, {
      slug, buyer_code: linkCode, aff: affParam || null, session_id: sessionId || null, ...body,
    })
      .then((r) => {
        if (keyRef.current !== key) return;
        const d = r.data || {};
        const next = d.state ? (d.buyer || null) : undefined;
        if (onDone) onDone(next);
        setLive(next);
      })
      .catch(() => {});
  }, [slug, linkCode, affParam, sessionId]);

  useEffect(() => {
    keyRef.current = "";
    geoRef.current = undefined;
    setLive(undefined);
    // A buyer's own link keeps showing that buyer; only the state the visitor picks can switch it.
    if (!enabled || !useLocation || linkCode) return;
    keyRef.current = "geo";
    ask({}, "geo", (v) => { geoRef.current = v; });
  }, [enabled, useLocation, ask, linkCode]);

  const onAnswers = useCallback((a) => {
    const state = String(a?.state || "").toUpperCase();
    const zipRaw = String(a?.zip || "");
    const zip = /^\d{5}/.test(zipRaw) ? zipRaw.slice(0, 5) : "";
    const key = state ? [state, a?.case_type || "", zip].join("|") : "geo";
    if (key === keyRef.current) return;
    keyRef.current = key;
    if (!state) { setLive(geoRef.current); return; }
    const answers = Object.fromEntries(ANSWER_KEYS.map((k) => [k, a?.[k] ?? null]));
    ask({ state, zip: zip || null, case_type: a?.case_type || null, ...answers }, key);
  }, [ask]);

  return { liveBuyer: enabled ? live : undefined, onAnswers: enabled ? onAnswers : null };
}

const SHOWN_KEYS = ["firm_name", "phone", "brand_color", "logo_url", "tcpa_language", "privacy_url", "terms_url"];

/** Company details to render: the page as loaded, the neutral funnel, or the live firm over the neutral funnel. */
export function shownDetails(page, activeBuyer) {
  const usingLink = Boolean(page.buyer && activeBuyer && activeBuyer.code === page.buyer.code);
  if (!(page.is_global_funnel || page.buyer_lander) || usingLink) return { shown: page, usingLink };
  const neutral = page.funnel_defaults || page;
  if (!activeBuyer) return { shown: neutral, usingLink };
  const merged = { ...neutral };
  SHOWN_KEYS.forEach((k) => { merged[k] = activeBuyer[k] || neutral[k] || null; });
  return { shown: merged, usingLink };
}
